import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AddressPicker, type AddressValue } from '@/components/address-picker'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useClients, clientName, type Client } from '@/hooks/use-clients'
import { CsvImportModal } from '@/components/csv-import'
import { Page, PageHeader, Card, Table, Trow, Avatar, TabPills, Pagination, SearchInput, SkeletonRows, EmptyState, btnPrimary, btnSecondary } from '@/components/rm'
import { Modal, Field, Input } from '@/components/rm/form'
import { money } from '@/lib/format'

export const Route = createFileRoute('/_app/clients')({ component: ClientsPage })

const COLS = '2fr 130px 90px 130px 130px'
const PAGE = 15
// PostgREST or() filters are comma/paren delimited — strip those from user input.
const safe = (s: string) => s.replace(/[,()]/g, ' ').trim()
const ago = (iso: string | null) => {
  if (!iso) return '—'
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (d <= 0) return 'Today'
  if (d === 1) return 'Yesterday'
  if (d < 7) return `${d} days ago`
  if (d < 14) return '1 week ago'
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`
  return `${Math.floor(d / 30)} mo ago`
}

function ClientsPage() {
  const navigate = useNavigate()
  const { create, importContacts } = useClients()
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState('all')
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const reset = () => setPage(0)

  // Tab counts — cheap head queries, independent of the current page.
  // Tabs are All / Active / Lead: `status` only ever holds 'active' or 'lead' in
  // practice (and defaults to 'active'), so the spec's "Inactive" tab would have
  // shipped permanently empty.
  const counts = useQuery({
    queryKey: ['clients', 'counts'],
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const base = () => supabase.from('contact').select('id', head).is('deleted_at', null)
      const [all, active, lead] = await Promise.all([
        base(),
        base().or('status.is.null,status.eq.active'),
        base().eq('status', 'lead'),
      ])
      return { all: all.count ?? 0, active: active.count ?? 0, lead: lead.count ?? 0 }
    },
  })

  // Server-side filtered + paginated page (mirrors the jobs list).
  const clientsPage = useQuery({
    queryKey: ['clients', 'page', { search, tab, page }],
    queryFn: async () => {
      let q = supabase.from('contact').select('*', { count: 'exact' }).is('deleted_at', null)
      if (tab === 'active') q = q.or('status.is.null,status.eq.active')
      else if (tab === 'lead') q = q.eq('status', 'lead')

      const s = safe(search)
      if (s) {
        q = q.or([
          `first_name.ilike.%${s}%`, `last_name.ilike.%${s}%`, `organization.ilike.%${s}%`,
          `email.ilike.%${s}%`, `phone.ilike.%${s}%`, `city.ilike.%${s}%`,
        ].join(','))
      }

      const { data, error, count } = await q
        .order('first_name', { ascending: true })
        .range(page * PAGE, page * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as Client[], count: count ?? 0 }
    },
  })
  const rows = clientsPage.data?.rows ?? []
  const c = counts.data

  // Per-client aggregates, scoped to the rows actually on screen. Previously this
  // pulled every task and every invoice in the company to build the whole map.
  const pageIds = rows.map((r) => r.id)
  const stats = useQuery({
    queryKey: ['clients', 'stats', pageIds],
    enabled: pageIds.length > 0,
    queryFn: async () => {
      const [tasks, billed] = await Promise.all([
        supabase.from('task').select('contact_id, created_at').eq('is_draft', false).is('deleted_at', null).in('contact_id', pageIds),
        // Lifetime value = everything billed (issued invoices), not just paid —
        // a long-standing client with open invoices shouldn't read as "$0".
        supabase.from('customer_invoice').select('contact_id, amount_paid, total, status').is('deleted_at', null)
          .not('status', 'in', '(draft,void)').in('contact_id', pageIds),
      ])
      const m: Record<string, { jobs: number; last: string | null; ltv: number }> = {}
      ;(tasks.data ?? []).forEach((t) => {
        const r = t as { contact_id: string | null; created_at: string | null }
        if (!r.contact_id) return
        const e = (m[r.contact_id] ||= { jobs: 0, last: null, ltv: 0 })
        e.jobs++
        if (r.created_at && (!e.last || r.created_at > e.last)) e.last = r.created_at
      })
      ;(billed.data ?? []).forEach((i) => {
        const r = i as { contact_id: string | null; amount_paid: number | null; total: number | null }
        if (!r.contact_id) return
        const e = (m[r.contact_id] ||= { jobs: 0, last: null, ltv: 0 })
        e.ltv += Number(r.total ?? r.amount_paid ?? 0)
      })
      return m
    },
  })

  return (
    <Page>
      <PageHeader
        title="Clients"
        subtitle={`${clientsPage.data?.count ?? 0} clients in this view`}
        actions={<><button className={btnSecondary} onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" /> Import CSV</button><button className={btnPrimary} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New client</button></>}
      />
      <div className="mb-[18px] mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <TabPills active={tab} onChange={(v) => { setTab(v); reset() }} chips={[
          { id: 'all', label: 'All', count: c?.all },
          { id: 'active', label: 'Active', count: c?.active },
          { id: 'lead', label: 'Leads', count: c?.lead },
        ]} />
        <div className="flex flex-1 items-center justify-end gap-2">
          <SearchInput value={search} onChange={(v) => { setSearch(v); reset() }} placeholder="Search clients…" />
        </div>
      </div>

      {clientsPage.isLoading ? (
        <Card><SkeletonRows count={6} /></Card>
      ) : rows.length === 0 ? (
        <Card><EmptyState label={(c?.all ?? 0) === 0 ? 'No clients yet.' : 'No clients match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New client' : undefined} onCta={() => setOpen(true)} /></Card>
      ) : (
        <Table cols={COLS} header={<><span>Client</span><span>Type</span><span className="text-center">Jobs</span><span>Last job</span><span className="text-right">Lifetime value</span></>}>
          {rows.map((c, i) => {
            const st = stats.data?.[c.id]
            const type = c.organization ? 'Commercial' : 'Residential'
            return (
              <Trow key={c.id} cols={COLS} last={i === rows.length - 1} onClick={() => navigate({ to: '/clients/$id', params: { id: c.id } })}>
                <div className="flex items-center gap-[11px]">
                  <Avatar name={clientName(c)} seed={i} />
                  <div className="min-w-0">
                    <div className="truncate font-bold text-token-primary">{clientName(c)}</div>
                    <div className="truncate text-[12px] text-token-secondary">{c.city || c.organization || '—'}</div>
                  </div>
                </div>
                <span className="text-token-secondary">{type}</span>
                <span className="text-center font-bold tabular-nums text-token-primary">{st?.jobs ?? 0}</span>
                <span className="text-token-secondary">{ago(st?.last ?? null)}</span>
                <span className={`font-display text-right font-bold tabular-nums ${st?.ltv ? 'text-token-primary' : 'text-token-muted'}`}>{st?.ltv ? money(st.ltv) : '—'}</span>
              </Trow>
            )
          })}
          <Pagination page={page} pageSize={PAGE} total={clientsPage.data?.count ?? 0} onPage={setPage} />
        </Table>
      )}

      <NewClientModal open={open} onClose={() => setOpen(false)} onCreate={create.mutateAsync} pending={create.isPending} />

      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import clients"
        fields={[
          { key: 'first_name', label: 'First name' },
          { key: 'last_name', label: 'Last name' },
          { key: 'organization', label: 'Business' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'address', label: 'Address' },
        ]}
        onImport={async (rows) => {
          const r = await importContacts.mutateAsync(rows)
          if (r.inserted) toast.success(`${r.inserted} client${r.inserted === 1 ? '' : 's'} imported`)
          return r
        }}
      />
    </Page>
  )
}

function NewClientModal({ open, onClose, onCreate, pending }: { open: boolean; onClose: () => void; onCreate: (p: Partial<Client>) => Promise<Client>; pending: boolean }) {
  const [f, setF] = useState({ first_name: '', last_name: '', organization: '', email: '', phone: '',
    address: null as string | null, line1: null as string | null, line2: null as string | null,
    city: null as string | null, state: null as string | null, postal_code: null as string | null,
    manual_address: false, latitude: null as number | null, longitude: null as number | null })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })

  const submit = async () => {
    if (!f.first_name.trim() && !f.last_name.trim() && !f.organization.trim()) return toast.error('Enter a name or business.')
    try {
      await onCreate({
        first_name: f.first_name.trim() || null, last_name: f.last_name.trim() || null,
        organization: f.organization.trim() || null, email: f.email.trim() || null, phone: f.phone.trim() || null,
        contact_type: 'client', status: 'active',
        // Same structured columns the run sheet, job list and map link read. A client added
        // here previously had no address at all, so any job against them had nowhere to send
        // the crew until someone went back and edited the record.
        address: f.address, line1: f.line1, line2: f.line2,
        city: f.city, state: f.state, postal_code: f.postal_code,
        manual_address: f.manual_address, latitude: f.latitude, longitude: f.longitude,
      })
      setF({ first_name: '', last_name: '', organization: '', email: '', phone: '',
    address: null as string | null, line1: null as string | null, line2: null as string | null,
    city: null as string | null, state: null as string | null, postal_code: null as string | null,
    manual_address: false, latitude: null as number | null, longitude: null as number | null })
      onClose()
      toast.success('Client added')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  return (
    <Modal open={open} onClose={onClose} title="New client"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} onClick={submit} disabled={pending}>{pending ? 'Adding…' : 'Add client'}</button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="First name"><Input value={f.first_name} onChange={set('first_name')} placeholder="Jane" /></Field>
          <Field label="Last name"><Input value={f.last_name} onChange={set('last_name')} placeholder="Smith" /></Field>
        </div>
        <Field label="Business / organisation"><Input value={f.organization} onChange={set('organization')} placeholder="Santos Trading Inc." /></Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Email"><Input value={f.email} onChange={set('email')} placeholder="jane@acme.com" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={set('phone')} placeholder="0917 123 4567" /></Field>
        </div>
        <AddressPicker
          value={{ address: f.address, line1: f.line1, line2: f.line2, city: f.city,
                   state: f.state, postal_code: f.postal_code, manual_address: f.manual_address }}
          onChange={(a: AddressValue) => setF((prev) => ({
            ...prev, address: a.address, line1: a.line1, line2: a.line2, city: a.city,
            state: a.state, postal_code: a.postal_code, manual_address: a.manual_address,
            latitude: a.latitude ?? null, longitude: a.longitude ?? null,
          }))}
        />
      </div>
    </Modal>
  )
}
